/**
 * Resend provisioning for a scaffolded storefront.
 *
 * The storefront needs one long-lived, send-only credential in
 * `RESEND_API_KEY`. Getting it used to mean leaving the operator staring at an
 * empty variable with no instruction attached, so this does what `carrier site
 * clerk` does for Clerk: the tiers run best-first and every one of them ends at
 * the same place, a key written into `.env.local` and proved to work.
 *
 *   1. `oauth`      — Resend's OAuth 2.1 + PKCE flow. The operator approves in
 *                     their own browser, and we mint a key on their account.
 *   2. `discovered` — a usable `re_` key already on the box (env or .env).
 *   3. `paste`      — open the API-keys page and take a pasted key. Only when
 *                     a human is actually at the terminal.
 *   4. `manual`     — nothing to go on: return the exact steps and stop.
 *
 * A note on why OAuth is used to mint a key rather than to send mail. Resend's
 * access tokens live 15 minutes and its refresh tokens rotate on every use
 * (docs: "Building an OAuth client for Resend"). Handing a rotating refresh
 * token to a Next.js app spread over serverless instances is a race with a
 * revoked grant at the end of it. So the browser flow is used once, for the
 * one thing it is uniquely good at — proving the operator owns the account —
 * and its product is an ordinary `sending_access` key. The grant is revoked
 * immediately afterwards; nothing long-lived is kept beyond the key itself.
 *
 * Endpoints below are Resend's published ones:
 *   POST https://api.resend.com/oauth/register   (dynamic client registration)
 *   GET  https://api.resend.com/oauth/authorize
 *   POST https://api.resend.com/oauth/token
 *   POST https://api.resend.com/oauth/revoke
 *   POST https://api.resend.com/api-keys
 */
import { createHash, randomBytes } from "node:crypto";
import { createServer } from "node:http";
import type { AddressInfo } from "node:net";

const RESEND_API = "https://api.resend.com";
export const RESEND_API_KEYS_URL = "https://resend.com/api-keys";
export const RESEND_DOMAINS_URL = "https://resend.com/domains";

/** Resend rejects requests with no User-Agent as 403, which reads as a bad key. */
const USER_AGENT = "carrier-cli";

/** The client name the operator sees on Resend's consent screen. */
const CLIENT_NAME = "Carrier storefront setup";

/**
 * `full_access` is needed, and only because creating an API key is not a
 * send-only route. The key this mints is `sending_access`, which is the scope
 * the storefront actually runs on.
 */
const OAUTH_SCOPE = "full_access";

/**
 * Loopback redirect. Registered without a port because RFC 8252 lets a native
 * client vary the port; `127.0.0.1` and `localhost` are different hosts to an
 * authorization server, so whichever is registered is the one we must serve.
 */
const REDIRECT_PATH = "/oauth/callback";
const REDIRECT_HOST = "127.0.0.1";

export type ResendTier = "oauth" | "discovered" | "paste" | "manual";

export interface ResendCredentials {
  apiKey: string;
  tier: ResendTier;
  /** Set when we minted the key ourselves. */
  keyId?: string;
  /** Set when the key was restricted to a single verified domain. */
  domain?: string;
}

export interface ResendProvisionResult {
  ok: boolean;
  tier: ResendTier;
  credentials?: ResendCredentials;
  /** Why a better tier did not run, carried forward for the operator to read. */
  reason?: string;
  /** Operator-facing steps, populated for the `manual` tier. */
  guidance?: string[];
}

type Env = Record<string, string | undefined>;
type FetchLike = typeof fetch;

/** Shape check only — never proves the key is live. */
export function looksResendKey(key: string): boolean {
  return /^re_[A-Za-z0-9_-]{8,}$/.test(key.trim());
}

/* ─── OAuth primitives ──────────────────────────────────────────────────── */

export interface PkcePair {
  verifier: string;
  challenge: string;
}

function base64url(input: Buffer): string {
  return input.toString("base64").replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

export function pkcePair(): PkcePair {
  const verifier = base64url(randomBytes(64));
  return { verifier, challenge: base64url(createHash("sha256").update(verifier).digest()) };
}

export function buildAuthorizeUrl(opts: {
  clientId: string;
  redirectUri: string;
  state: string;
  challenge: string;
  scope?: string;
}): string {
  const url = new URL(`${RESEND_API}/oauth/authorize`);
  url.searchParams.set("client_id", opts.clientId);
  url.searchParams.set("response_type", "code");
  url.searchParams.set("redirect_uri", opts.redirectUri);
  url.searchParams.set("scope", opts.scope ?? OAUTH_SCOPE);
  url.searchParams.set("state", opts.state);
  url.searchParams.set("code_challenge", opts.challenge);
  url.searchParams.set("code_challenge_method", "S256");
  return url.toString();
}

async function resendFetch(
  fetchImpl: FetchLike,
  path: string,
  init: { method?: string; token?: string; json?: unknown; form?: Record<string, string> } = {},
): Promise<{ ok: boolean; status: number; json?: unknown; error?: string }> {
  const headers: Record<string, string> = { "User-Agent": USER_AGENT, Accept: "application/json" };
  if (init.token) headers.Authorization = `Bearer ${init.token}`;

  let body: string | undefined;
  if (init.form) {
    headers["Content-Type"] = "application/x-www-form-urlencoded";
    body = new URLSearchParams(init.form).toString();
  } else if (init.json !== undefined) {
    headers["Content-Type"] = "application/json";
    body = JSON.stringify(init.json);
  }

  try {
    const res = await fetchImpl(`${RESEND_API}${path}`, {
      method: init.method ?? "GET",
      headers,
      body,
    });
    const text = await res.text();
    let json: unknown;
    try {
      json = text ? JSON.parse(text) : undefined;
    } catch {
      json = undefined;
    }
    if (!res.ok) {
      return { ok: false, status: res.status, json, error: resendError(json) ?? text.slice(0, 300) };
    }
    return { ok: true, status: res.status, json };
  } catch (e: unknown) {
    return { ok: false, status: 0, error: e instanceof Error ? e.message : String(e) };
  }
}

/** Resend returns `{ message }` or `{ error_description }` on failure. */
function resendError(json: unknown): string | undefined {
  if (typeof json !== "object" || json === null) return undefined;
  const body = json as { message?: string; error_description?: string; error?: string };
  return body.message ?? body.error_description ?? body.error;
}

/* ─── The pieces the flow is made of ─────────────────────────────────────── */

/**
 * Register as a public client.
 *
 * The redirect is registered without a port on purpose: RFC 8252 lets a
 * loopback client vary the port, so one registration covers every run. Pinning
 * the ephemeral port here would burn a fresh client registration each time the
 * command is used.
 */
export async function registerOauthClient(
  fetchImpl: FetchLike,
): Promise<{ ok: true; clientId: string } | { ok: false; reason: string }> {
  const registeredRedirect = `http://${REDIRECT_HOST}${REDIRECT_PATH}`;

  const res = await resendFetch(fetchImpl, "/oauth/register", {
    method: "POST",
    json: {
      client_name: CLIENT_NAME,
      redirect_uris: [registeredRedirect],
      grant_types: ["authorization_code", "refresh_token"],
      response_types: ["code"],
      token_endpoint_auth_method: "none",
      scope: OAUTH_SCOPE,
    },
  });
  if (!res.ok) {
    return { ok: false, reason: `Resend rejected client registration: ${res.error ?? res.status}` };
  }
  const clientId = (res.json as { client_id?: string } | undefined)?.client_id;
  if (!clientId) return { ok: false, reason: "Resend registered no client_id." };
  return { ok: true, clientId };
}

export async function exchangeCode(
  fetchImpl: FetchLike,
  opts: { clientId: string; code: string; redirectUri: string; verifier: string },
): Promise<{ ok: true; accessToken: string; refreshToken?: string } | { ok: false; reason: string }> {
  const res = await resendFetch(fetchImpl, "/oauth/token", {
    method: "POST",
    form: {
      grant_type: "authorization_code",
      client_id: opts.clientId,
      code: opts.code,
      redirect_uri: opts.redirectUri,
      code_verifier: opts.verifier,
    },
  });
  if (!res.ok) return { ok: false, reason: `Token exchange failed: ${res.error ?? res.status}` };
  const body = res.json as { access_token?: string; refresh_token?: string } | undefined;
  if (!body?.access_token) return { ok: false, reason: "Resend returned no access token." };
  return { ok: true, accessToken: body.access_token, refreshToken: body.refresh_token };
}

/**
 * Give the grant back. The key we minted outlives it and is all the storefront
 * needs, so holding a refresh token afterwards would be keeping a credential
 * for no reason.
 */
export async function revokeGrant(
  fetchImpl: FetchLike,
  opts: { clientId: string; refreshToken: string },
): Promise<void> {
  await resendFetch(fetchImpl, "/oauth/revoke", {
    method: "POST",
    form: {
      client_id: opts.clientId,
      token: opts.refreshToken,
      token_type_hint: "refresh_token",
    },
  }).catch(() => undefined);
}

/**
 * Find the operator's own verified domain, so the minted key can be pinned to
 * it. A send-only key that can only send from one domain is the smallest thing
 * that does the job, and it is exactly the domain the from-address is built
 * from.
 */
export async function findVerifiedDomainId(
  fetchImpl: FetchLike,
  accessToken: string,
  domain: string,
): Promise<string | undefined> {
  const wanted = domain.trim().replace(/^https?:\/\//, "").replace(/\/+$/, "").toLowerCase();
  if (!wanted) return undefined;
  const res = await resendFetch(fetchImpl, "/domains", { token: accessToken });
  if (!res.ok) return undefined;
  const list = (res.json as { data?: Array<{ id?: string; name?: string; status?: string }> })?.data;
  const match = list?.find((d) => d.name?.toLowerCase() === wanted && d.status === "verified");
  return match?.id;
}

export async function createSendingKey(
  fetchImpl: FetchLike,
  accessToken: string,
  opts: { name: string; domainId?: string },
): Promise<{ ok: true; token: string; id?: string } | { ok: false; reason: string }> {
  const res = await resendFetch(fetchImpl, "/api-keys", {
    method: "POST",
    token: accessToken,
    json: {
      // Resend caps the name at 50 characters.
      name: opts.name.slice(0, 50),
      permission: "sending_access",
      ...(opts.domainId ? { domain_id: opts.domainId } : {}),
    },
  });
  if (!res.ok) return { ok: false, reason: `Could not create an API key: ${res.error ?? res.status}` };
  const body = res.json as { token?: string; id?: string } | undefined;
  if (!body?.token) return { ok: false, reason: "Resend created a key but returned no token." };
  return { ok: true, token: body.token, id: body.id };
}

/**
 * Prove a key works, without sending anybody an email.
 *
 * A `sending_access` key can only reach `POST /emails`, so there is no
 * harmless read to call. What there is: authentication runs before payload
 * validation. An empty body therefore returns a validation error on a good key
 * and 401/403 on a bad one, which is the whole question. Nothing is sent
 * because nothing in the body could be sent.
 */
export async function validateResendKey(
  fetchImpl: FetchLike,
  key: string,
): Promise<{ ok: boolean; reason?: string }> {
  if (!looksResendKey(key)) return { ok: false, reason: "That does not look like a Resend key (re_…)." };
  const res = await resendFetch(fetchImpl, "/emails", { method: "POST", token: key, json: {} });
  if (res.status === 401) return { ok: false, reason: "Resend says that key is missing or malformed." };
  if (res.status === 403) return { ok: false, reason: "Resend rejected that key as invalid." };
  if (res.status === 0) return { ok: false, reason: `Could not reach Resend: ${res.error ?? "network error"}` };
  return { ok: true };
}

/* ─── The loopback leg ───────────────────────────────────────────────────── */

export interface CallbackResult {
  ok: boolean;
  code?: string;
  reason?: string;
}

export interface LoopbackServer {
  redirectUri: string;
  /** Resolves on the first callback, or on timeout. Always closes the server. */
  waitForCode(state: string, timeoutMs: number): Promise<CallbackResult>;
  close(): void;
}

/**
 * A single-use HTTP listener on loopback.
 *
 * Bound to 127.0.0.1 explicitly, never 0.0.0.0: for the length of the consent
 * screen this process is accepting an authorization code over plain HTTP, and
 * it must not be reachable from the network the operator is sitting on.
 */
export async function startLoopbackServer(): Promise<LoopbackServer> {
  let resolveCode: ((r: CallbackResult) => void) | null = null;

  const server = createServer((req, res) => {
    const url = new URL(req.url ?? "/", `http://${REDIRECT_HOST}`);
    if (url.pathname !== REDIRECT_PATH) {
      res.writeHead(404).end();
      return;
    }
    const error = url.searchParams.get("error");
    const code = url.searchParams.get("code");
    const state = url.searchParams.get("state");
    res.writeHead(200, { "Content-Type": "text/html; charset=utf-8" });
    res.end(
      `<!doctype html><meta charset="utf-8"><title>Carrier</title>` +
        `<body style="font-family:system-ui;padding:3rem;text-align:center">` +
        `<p>${error ? "Authorization was refused." : "Connected. You can close this tab."}</p>`,
    );
    resolveCode?.(
      error
        ? { ok: false, reason: `Resend returned "${error}".` }
        : code
          ? { ok: true, code, reason: state ?? undefined }
          : { ok: false, reason: "Resend sent no authorization code." },
    );
  });

  await new Promise<void>((resolve, reject) => {
    server.once("error", reject);
    server.listen(0, REDIRECT_HOST, resolve);
  });

  const port = (server.address() as AddressInfo).port;
  const redirectUri = `http://${REDIRECT_HOST}:${port}${REDIRECT_PATH}`;

  return {
    redirectUri,
    close: () => server.close(),
    waitForCode(state, timeoutMs) {
      return new Promise<CallbackResult>((resolve) => {
        const done = (r: CallbackResult) => {
          clearTimeout(timer);
          resolveCode = null;
          server.close();
          resolve(r);
        };
        const timer = setTimeout(
          () => done({ ok: false, reason: "Timed out waiting for the browser." }),
          timeoutMs,
        );
        resolveCode = (r) => {
          // `reason` carries the returned state on the success path; a mismatch
          // means the callback did not come from the request we made.
          if (r.ok && r.reason !== state) {
            done({ ok: false, reason: "The browser callback did not match this request." });
            return;
          }
          done(r.ok ? { ok: true, code: r.code } : r);
        };
      });
    },
  };
}

/* ─── Tiers ──────────────────────────────────────────────────────────────── */

/** Tier 2 — a usable key already present in the environment. */
export function discoverResendKey(env: Env): ResendCredentials | undefined {
  const key = env.RESEND_API_KEY?.trim();
  if (!key || !looksResendKey(key)) return undefined;
  return { apiKey: key, tier: "discovered" };
}

export const MANUAL_STEPS = [
  `Open ${RESEND_API_KEYS_URL} and create an API key.`,
  `Give it Sending access, and restrict it to your sending domain if that domain is verified at ${RESEND_DOMAINS_URL}.`,
  "Put the key in the storefront's .env.local as RESEND_API_KEY.",
  "Re-run `carrier site deploy` — the key is pushed to the deploy target automatically.",
];

/**
 * The bits of the outside world the flow needs, injected so the tiers are
 * testable without a browser, a socket or a Resend account.
 */
export interface ResendDeps {
  fetch: FetchLike;
  openBrowser(url: string): Promise<{ ok: boolean; hint: string }>;
  startLoopback(): Promise<LoopbackServer>;
  /** Show the authorization URL to the operator (browser could not open). */
  notify(message: string): void;
  /** Absent when there is no human at the terminal — the paste tier is skipped. */
  promptForKey?(url: string): Promise<string | null>;
}

export interface ResendProvisionOptions {
  /** Brand name, used to name the key on the operator's Resend account. */
  name: string;
  /** Sending domain, used to pin the key when it is verified at Resend. */
  domain?: string;
  env: Env;
  deps: ResendDeps;
  /** Skip the browser leg entirely (CI, `--no-browser`). */
  noBrowser?: boolean;
  /** How long to hold the loopback listener open. */
  timeoutMs?: number;
}

/**
 * Run the tiers in order and return the first that produces a working key.
 *
 * Never throws, and never reports a key it has not proved: every tier ends at
 * `validateResendKey`, so "configured" always means Resend accepted it.
 */
export async function provisionResend(
  opts: ResendProvisionOptions,
): Promise<ResendProvisionResult> {
  const { deps } = opts;
  const notes: string[] = [];

  // Reusing a key that already works beats minting another one.
  const discovered = discoverResendKey(opts.env);
  if (discovered) {
    const check = await validateResendKey(deps.fetch, discovered.apiKey);
    if (check.ok) return { ok: true, tier: "discovered", credentials: discovered };
    notes.push(`A RESEND_API_KEY was already set but did not work: ${check.reason}`);
  }

  if (!opts.noBrowser) {
    const viaBrowser = await oauthTier(opts);
    if (viaBrowser.ok) {
      return { ...viaBrowser, reason: notes.length ? notes.join(" ") : undefined };
    }
    notes.push(viaBrowser.reason ?? "The browser authorization did not complete.");
  }

  if (deps.promptForKey) {
    await deps.openBrowser(RESEND_API_KEYS_URL);
    const pasted = (await deps.promptForKey(RESEND_API_KEYS_URL))?.trim();
    if (pasted) {
      const check = await validateResendKey(deps.fetch, pasted);
      if (check.ok) {
        return {
          ok: true,
          tier: "paste",
          credentials: { apiKey: pasted, tier: "paste" },
          reason: notes.length ? notes.join(" ") : undefined,
        };
      }
      notes.push(check.reason ?? "The pasted key did not work.");
    }
  }

  return { ok: false, tier: "manual", reason: notes.join(" "), guidance: MANUAL_STEPS };
}

async function oauthTier(opts: ResendProvisionOptions): Promise<ResendProvisionResult> {
  const { deps } = opts;
  let server: LoopbackServer;
  try {
    server = await deps.startLoopback();
  } catch (e: unknown) {
    return {
      ok: false,
      tier: "oauth",
      reason: `Could not open a local callback listener: ${e instanceof Error ? e.message : String(e)}`,
    };
  }

  try {
    const registered = await registerOauthClient(deps.fetch);
    if (!registered.ok) return { ok: false, tier: "oauth", reason: registered.reason };

    const { verifier, challenge } = pkcePair();
    const state = base64url(randomBytes(24));
    const authorizeUrl = buildAuthorizeUrl({
      clientId: registered.clientId,
      redirectUri: server.redirectUri,
      state,
      challenge,
    });

    const opened = await deps.openBrowser(authorizeUrl);
    deps.notify(
      opened.ok
        ? "Approve the connection in your browser — this waits for you."
        : `Open this to approve the connection:\n  ${authorizeUrl}`,
    );

    const callback = await server.waitForCode(state, opts.timeoutMs ?? 180_000);
    if (!callback.ok || !callback.code) {
      return { ok: false, tier: "oauth", reason: callback.reason ?? "No authorization code." };
    }

    const token = await exchangeCode(deps.fetch, {
      clientId: registered.clientId,
      code: callback.code,
      redirectUri: server.redirectUri,
      verifier,
    });
    if (!token.ok) return { ok: false, tier: "oauth", reason: token.reason };

    const domainId = opts.domain
      ? await findVerifiedDomainId(deps.fetch, token.accessToken, opts.domain)
      : undefined;

    const created = await createSendingKey(deps.fetch, token.accessToken, {
      name: `${opts.name} storefront`,
      domainId,
    });

    if (token.refreshToken) {
      await revokeGrant(deps.fetch, {
        clientId: registered.clientId,
        refreshToken: token.refreshToken,
      });
    }

    if (!created.ok) return { ok: false, tier: "oauth", reason: created.reason };

    const check = await validateResendKey(deps.fetch, created.token);
    if (!check.ok) {
      return { ok: false, tier: "oauth", reason: `Resend minted a key that it then rejected: ${check.reason}` };
    }

    return {
      ok: true,
      tier: "oauth",
      credentials: {
        apiKey: created.token,
        tier: "oauth",
        keyId: created.id,
        domain: domainId ? opts.domain : undefined,
      },
    };
  } finally {
    server.close();
  }
}
